import React from 'react';
import { Route, Routes, BrowserRouter } from 'react-router-dom';
import { Spin, ConfigProvider, App as AntdApp } from 'antd';
import zhCN from 'antd/lib/locale/zh_CN';
import { LoadingOutlined } from '@ant-design/icons';
import { Provider, observer } from 'mobx-react';
import { AppStore } from './store';
import { baseRoutes } from './routers';
import { getBaseUrl } from './utils';
import { AppProvider } from './context/app-contex';
import './app.less';
import { sensorsInit } from './utils/sensors';
import AppVersionProvider from './AppVersionProvider';
import queryDomMethods from './utils/queryDomMethods';
import { IMicroAppProps } from './types';
import MicroComponents from '@/pages/micro-components';
import useAppInitialization from './utils/useAppInitialization';
import './i18n'; // 加载国际化配置

// 埋点
sensorsInit();

// store
const store = new AppStore();

// loading
const Loading = (
    <div className="loading-container">
        <Spin fullscreen indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} size="large" />
    </div>
);

const AntdConfigProvider: React.FC<{ themeStore: AppStore['themeStore']; children: React.ReactNode }> = ({ themeStore, children }) => (
    <ConfigProvider
        iconPrefixCls="anticon5"
        prefixCls="ant5"
        locale={zhCN}
        getPopupContainer={queryDomMethods.querySubTikuRoot}
        theme={{ token: { colorPrimary: themeStore.theme.mainColor, borderRadius: 8 } }}
    >
        {children}
    </ConfigProvider>
);

function App(props: IMicroAppProps) {
    useAppInitialization(props, store);

    const appProviderValue = {
        pushQuestionCallback: props.pushQuestionCallback,
        answerFunction: props.onClickAnswerQuestion,
        openEquityModal: props.handleOpenEquityModal,
        submitCallback: props.submitCallback,
        singleQuestionCallback: props.singleQuestionCallback,
        businessParam: props.businessParam,
        questionBusinessParamMap: props.questionBusinessParamMap,
        sourceFromType: props.sourceFromType || 0,
    };

    if (props.microAppType) {
        return (
            <Provider store={store}>
                <AppVersionProvider>
                    <AntdConfigProvider themeStore={store.themeStore}>
                        <AntdApp
                            component={false}
                            message={{
                                getContainer: queryDomMethods.querySubTikuRoot,
                                prefixCls: 'ant5-message',
                            }}
                            notification={{
                                getContainer: queryDomMethods.querySubTikuRoot,
                                prefixCls: 'ant5-notification',
                            }}
                        >
                            <AppProvider value={appProviderValue}>
                                <MicroComponents {...props} />
                            </AppProvider>
                        </AntdApp>
                    </AntdConfigProvider>
                </AppVersionProvider>
            </Provider>
        );
    }

    return (
        <Provider store={store}>
            <AppVersionProvider>
                <AntdConfigProvider themeStore={store.themeStore}>
                    <AppProvider value={appProviderValue}>
                        <BrowserRouter basename={getBaseUrl()}>
                            <Routes>
                                {baseRoutes.map(item => (
                                    <Route
                                        key={item.path}
                                        path={item.path}
                                        element={
                                            <React.Suspense fallback={Loading}>
                                                <item.element key={item.path} {...(props as any)} />
                                            </React.Suspense>
                                        }
                                    />
                                ))}
                            </Routes>
                        </BrowserRouter>
                    </AppProvider>
                </AntdConfigProvider>
            </AppVersionProvider>
        </Provider>
    );
}

export default observer(App);
